// frontend/src/components/TreatmentPlanPickerModal.js

import React, { useState, useEffect, useMemo } from "react";
import {
  XMarkIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";

const CheckboxField = ({
  label,
  checked,
  onChange,
  price,
  products = [],
  showProducts = false,
  selectedProducts = {},
  onProductToggle,
  procedureId,
}) => (
  <label className="flex flex-col gap-1 cursor-pointer py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0"
        />
        <span className="text-[12px] text-slate-700">{label}</span>
      </div>
      {price !== undefined && price > 0 && <span className="text-[11px] font-semibold text-emerald-600">₹{price}</span>}
    </div>
    {showProducts && products && products.length > 0 && (
      <div className="ml-6 space-y-0.5">
        {products.map((prod, idx) => {
          const productKey = prod.product_id || prod._id || `prod-${idx}`;
          const compositeKey = procedureId ? `${procedureId}_${productKey}` : productKey;
          const stock = prod.product_details?.quantity_available ?? 0;
          const productName = prod.product_details?.name || prod.name || 'Unknown Product';
          const usageQty = prod.usage_qty || prod.quantity || 1;
          const unit = prod.product_details?.unit || prod.unit || '';
          const isChecked = selectedProducts[compositeKey] !== undefined
            ? selectedProducts[compositeKey]
            : true;

          return (
            <label key={compositeKey} className="flex items-center justify-between gap-2 py-0.5 px-1 rounded hover:bg-slate-50 cursor-pointer">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => onProductToggle && onProductToggle(prod, e.target.checked)}
                  className="w-3 h-3 rounded border-slate-300 text-emerald-500 focus:ring-emerald-400 focus:ring-offset-0"
                />
                <span className="text-[10px] text-slate-600">↳ {productName}</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-slate-400">
                <span>Usage: {usageQty} {unit}</span>
                <span className={stock > 0 ? 'text-emerald-600' : 'text-red-500'}>
                  Stock: {stock}
                </span>
              </div>
            </label>
          );
        })}
      </div>
    )}
  </label>
);

const TreatmentPlanPickerModal = ({ isOpen, onClose, onApply }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedProcedures, setSelectedProcedures] = useState({});
  const [selectedProducts, setSelectedProducts] = useState({});
  const [selectedProcedureProducts, setSelectedProcedureProducts] = useState({});

  useEffect(() => {
    if (isOpen) fetchPlans();
    else {
      setSelectedPlan(null);
      setSelectedProcedures({});
      setSelectedProducts({});
      setSelectedProcedureProducts({});
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(`/vendorvetclinicservice/get-treatment-plans?status=active`);
      if (response.code === 200) setPlans(response?.data || []);
    } catch (err) {
      console.error("Error fetching treatment plans:", err);
      toast.error("Failed to load treatment plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planSummary) => {
    setDetailLoading(true);
    setSelectedPlan(null);
    setSelectedProcedureProducts({});
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-treatment-plan/${planSummary._id}`
      );
      if (response.code === 200) {
        const data = response.data;
        const procedures = (data.procedures || []).map((p, idx) => {
          const procDoc = typeof p.procedure_id === "object" ? p.procedure_id : null;
          const price = p.override_price?.low ?? procDoc?.fixed_price ?? 0;
          const products = p.procedure_details?.products_with_inventory || [];

          const initialProductSelections = {};
          products.forEach((prod) => {
            const productKey = prod.product_id || prod._id || `prod-${idx}`;
            const compositeKey = `${p.procedure_id || idx}_${productKey}`;
            initialProductSelections[compositeKey] = true;
          });

          return {
            key: extractId(p.procedure_id) || idx,
            procedure_id: extractId(p.procedure_id),
            procedure_name: procDoc?.name || p.procedure_name || "Unknown Procedure",
            occurrences: p.occurrences || 1,
            price: price,
            procDoc,
            procedure_details: p.procedure_details || null,
            products: products,
            productSelections: initialProductSelections,
          };
        });

        const products = (data.products || []).map((item, idx) => {
          const prod = item.product_id && typeof item.product_id === "object" ? item.product_id : null;
          const details = item.product_details || null;
          const unitPrice = Number(
            item.price ||
              item.effective_sale_price ||
              details?.sale_price ||
              details?.mrp ||
              details?.effective_sale_price ||
              prod?.sale_price ||
              prod?.mrp ||
              0,
          );
          return {
            key: extractId(item.product_id) || idx,
            _id: extractId(item.product_id),
            name: details?.name || prod?.name || item.name || "Unknown Product",
            sku_number: details?.sku_number || prod?.sku_number || item.sku_number || "",
            unit: details?.unit || prod?.unit || item.unit || "",
            price: unitPrice,
            quantity: item.quantity || 1,
            usage_qty: item.usage_qty || "",
            usage_unit: item.usage_unit || "",
            brand: details?.brand_name || details?.product_brand_id?.name || "",
            product_details: details,
          };
        });

        const initialProcProductSelections = {};
        procedures.forEach((proc) => {
          if (proc.products && proc.products.length > 0) {
            proc.products.forEach((prod) => {
              const productKey = prod.product_id || prod._id;
              const compositeKey = `${proc.procedure_id || proc.key}_${productKey}`;
              initialProcProductSelections[compositeKey] = true;
            });
          }
        });

        setSelectedPlan({ ...planSummary, procedures, products });
        setSelectedProcedures(Object.fromEntries(procedures.map((p) => [p.key, true])));
        setSelectedProducts(Object.fromEntries(products.map((p) => [p.key, true])));
        setSelectedProcedureProducts(initialProcProductSelections);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load treatment plan details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleProcedureToggle = (procKey, checked) => {
    // Update procedure selection
    setSelectedProcedures((prev) => ({
      ...prev,
      [procKey]: checked,
    }));

    // Get the procedure object
    const procedure = selectedPlan?.procedures?.find(p => p.key === procKey);
    if (procedure && procedure.products) {
      const updates = {};
      procedure.products.forEach((prod) => {
        const productKey = prod.product_id || prod._id;
        const compositeKey = `${procedure.procedure_id || procKey}_${productKey}`;
        // Check products when procedure is checked, uncheck when unchecked
        updates[compositeKey] = checked;
      });
      setSelectedProcedureProducts((prev) => ({
        ...prev,
        ...updates,
      }));
    }
  };

  const handleProcedureProductToggle = (procKey, procId, product, checked) => {
    const productKey = product.product_id || product._id;
    const compositeKey = `${procId || procKey}_${productKey}`;
    setSelectedProcedureProducts((prev) => ({
      ...prev,
      [compositeKey]: checked,
    }));
  };

  const total = useMemo(() => {
    if (!selectedPlan) return 0;
    let total = 0;

    selectedPlan.procedures.forEach((p) => {
      if (selectedProcedures[p.key]) {
        total += p.price * p.occurrences;
      }
    });

    selectedPlan.products.forEach((p) => {
      if (selectedProducts[p.key]) {
        total += p.price * p.quantity;
      }
    });
    return total;
  }, [selectedPlan, selectedProcedures, selectedProducts]);

  const handleApply = () => {
    if (!selectedPlan) return;

    const procedures = selectedPlan.procedures.filter((p) => selectedProcedures[p.key]);
    const products = selectedPlan.products.filter((p) => selectedProducts[p.key]);

    const proceduresWithFilteredProducts = procedures.map((proc) => {
      if (proc.products && proc.products.length > 0) {
        const filteredProducts = proc.products.filter((prod) => {
          const productKey = prod.product_id || prod._id;
          const compositeKey = `${proc.procedure_id || proc.key}_${productKey}`;
          return selectedProcedureProducts[compositeKey] !== false;
        });
        return { ...proc, products: filteredProducts };
      }
      return proc;
    });

    onApply({
      planMeta: { _id: selectedPlan._id, name: selectedPlan.name },
      procedures: proceduresWithFilteredProducts,
      products
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-5 h-5 text-emerald-600" />
            <h2 className="text-base font-bold text-slate-800">Choose Treatment Plan</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading treatment plans...</p>
            </div>
          ) : plans.length === 0 ? (
            <p className="text-[12px] text-slate-400 text-center py-8">
              No saved treatment plans yet. You can still add services and products directly instead.
            </p>
          ) : (
            <>
              <div>
                <FieldLabel>Select a saved plan</FieldLabel>
                <div className="grid grid-cols-2 gap-2">
                  {plans.map((plan) => (
                    <button
                      key={plan._id}
                      onClick={() => handleSelectPlan(plan)}
                      className={`px-3 py-2 text-left text-[12px] font-medium rounded-xl border transition-all ${
                        selectedPlan?._id === plan._id
                          ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                          : "bg-white border-slate-200 text-slate-600 hover:border-emerald-200"
                      }`}
                    >
                      {plan.name}
                    </button>
                  ))}
                </div>
              </div>

              {detailLoading && (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
                </div>
              )}

              {selectedPlan && !detailLoading && (
                <div className="space-y-4 border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-slate-500">Check items to include:</p>
                    <p className="text-[11px] font-bold text-emerald-600">
                      Total: ₹{total.toFixed(2)}
                    </p>
                  </div>

                  {selectedPlan.procedures.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Services</p>
                      <div className="space-y-1">
                        {selectedPlan.procedures.map((proc) => {
                          const products = proc.products || [];

                          return (
                            <CheckboxField
                              key={proc.key}
                              label={`${proc.procedure_name}${proc.occurrences > 1 ? ` × ${proc.occurrences}` : ""}`}
                              price={proc.price}
                              checked={!!selectedProcedures[proc.key]}
                              onChange={(v) => handleProcedureToggle(proc.key, v)}
                              products={products}
                              showProducts={true}
                              selectedProducts={selectedProcedureProducts}
                              onProductToggle={(prod, checked) =>
                                handleProcedureProductToggle(proc.key, proc.procedure_id, prod, checked)
                              }
                              procedureId={proc.procedure_id}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedPlan.products.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Products</p>
                      <div className="space-y-1">
                        {selectedPlan.products.map((prod) => (
                          <CheckboxField
                            key={prod.key}
                            label={`${prod.name}${prod.quantity > 1 ? ` × ${prod.quantity}` : ""}`}
                            price={prod.price}
                            checked={!!selectedProducts[prod.key]}
                            onChange={(v) => setSelectedProducts((prev) => ({ ...prev, [prod.key]: v }))}
                            products={[]}
                            showProducts={false}
                            procedureId={null}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 bg-slate-50">
          <div className="text-[13px] font-bold text-emerald-700">
            {selectedPlan && `Selected Total: ₹${total.toFixed(2)}`}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-[12px] font-medium text-slate-500 hover:text-slate-700">
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!selectedPlan}
              className="flex items-center gap-2 px-4 py-2 text-[12px] font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckIcon className="w-3.5 h-3.5" />
              Apply Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function
const extractId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.$oid) return value.$oid;
  if (typeof value === "object" && value._id) return extractId(value._id);
  if (typeof value === "object" && value.toString && value.toString() !== "[object Object]") {
    return value.toString();
  }
  if (typeof value === "object" && value.id) return extractId(value.id);
  return null;
};

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

export default TreatmentPlanPickerModal;